"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ClipboardCheck, FileText, CalendarCheck, Building2, Info, Zap, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const pathwaySteps = [{
  step: "01",
  title: "Eligibility & Assessment",
  desc: "We review your commercial energy billing profile and operational footprint to check whether you may qualify for the €2,000 SEAI audit voucher.",
  icon: ClipboardCheck,
  points: ["Billing profile analysis"]
}, {
  step: "02",
  title: "Grant Application & SEAI Coordination",
  desc: "CMI can guide your business through the SEAI Energy Audit Voucher process, help confirm the information required and coordinate an appropriately qualified SEAI Registered Energy Auditor. The applicant completes the SEAI application in accordance with the scheme requirements.",
  icon: FileText,
  points: ["SEAI scheme guidance", "Required information confirmation"]
}, {
  step: "03",
  title: "Energy Audit & Actionable Report",
  desc: "We coordinate an appropriately qualified SEAI Registered Energy Auditor to conduct a meticulous on-site survey and identify high-return measures.",
  icon: CalendarCheck,
  points: ["On-site technical survey", "Prioritised investment roadmap"]
}];
export default function Voucher() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.09,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.2, 0.8, 0.2, 1]
      }
    }
  };
  return <section id="seai-energy-audit-voucher" data-nav="light" className="bg-background py-12 md:py-16 text-foreground relative overflow-hidden isolate">
      {/* Decorative background grid subtle accent */}
      <div aria-hidden="true" className="pointer-events-none absolute right-0 top-12 -z-10 h-96 w-96 opacity-15 blur-2xl">
        <div className="h-full w-full rounded-full bg-primary/20" />
      </div>

      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-14">
          <div className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary shadow-2xs mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <span>SEAI Grant Support Scheme</span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-foreground font-default">
            Could Your Business Qualify for a €2,000 SEAI Energy Audit Voucher?
          </h2>

          {/* Dual-Leaf Energy Rule Accent */}
          <div className="mx-auto my-4 h-0.5 w-20 rounded-lg bg-linear-to-r from-emerald-600 to-primary" />

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Eligible businesses can secure a €2,000 SEAI voucher towards an independent energy audit—the essential first step of your efficiency roadmap. Before committing capital to solar, heating, or lighting, an audit reveals where genuine savings lie, noting that the first obvious upgrade is not always the highest-return measure.
          </p>
        </div>

        {/* Main Content Layout */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Grant Feature Card */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="relative group">
              <div aria-hidden="true" className="absolute -inset-1 rounded-2xl bg-linear-to-b from-primary/10 to-emerald-500/10 opacity-70 blur-xs transition-[opacity] duration-300 group-hover:opacity-100" />
              
              <Card className="relative border-border bg-card shadow-md transition-[box-shadow,border-color] duration-300 hover:shadow-lg hover:border-primary/40 rounded-2xl overflow-hidden p-6 sm:p-8">
                <CardContent className="p-0 space-y-6">
                  {/* Top Badge & Metric */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                        SEAI Scheme Voucher
                      </span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl sm:text-5xl font-extrabold tracking-tight text-foreground font-default">
                          €2,000
                        </span>
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-primary/10 text-primary shrink-0">
                      <Zap className="size-6 text-primary" />
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Designed by the Sustainable Energy Authority of Ireland to identify practical, cost-effective energy improvements. CMI coordinates appropriately qualified SEAI Registered Energy Auditors and specialist technical contractors to guide your facility from initial survey to completed upgrades.
                  </p>

                  <div className="border-t border-border pt-5 space-y-3">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Building2 className="size-4 text-primary" />
                      Key qualification criteria
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      Eligibility is subject to the current SEAI Support Scheme for Energy Audits criteria. CMI can help your business check whether it may qualify for the €2,000 Energy Audit Voucher.
                    </p>
                  </div>

                  {/* Coordination Notice */}
                  <div className="rounded-lg bg-primary/5 p-3.5 border border-primary/20 flex items-start gap-3">
                    <Wrench className="size-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground font-medium">Independent Coordination:</strong> CMI coordinates appropriately qualified SEAI Registered Energy Auditors and specialist technical contractors to ensure all recommendations are commercially sound and technically validated.
                    </p>
                  </div>

                  {/* Eligibility & Scheme Disclaimer */}
                  <div className="rounded-lg bg-muted/50 p-3.5 border border-border flex items-start gap-3">
                    <Info className="size-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      If your business spends more than approximately €10,000 per year on electricity and gas combined, contact our team. Annual spend is an initial benchmark; grant approval remains subject to SEAI scheme guidelines and terms.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Right Column: Process Pathway & Seamless Continuation */}
          <motion.div variants={itemVariants} className="lg:col-span-7 space-y-4">
            <div className="space-y-4">
              {pathwaySteps.map((stepItem, i) => {
              const IconComponent = stepItem.icon;
              return <div key={stepItem.step} data-index={i} className="group relative rounded-xl border border-border bg-card p-5 sm:p-6 shadow-2xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
                      <div className="flex items-center sm:items-start gap-3 sm:gap-0 shrink-0">
                        <div className="flex size-10 sm:size-12 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold text-base sm:text-lg">
                          {stepItem.step}
                        </div>
                        <div className="sm:hidden font-semibold text-foreground text-base">
                          {stepItem.title}
                        </div>
                      </div>

                      <div className="space-y-2 grow">
                        <div className="hidden sm:flex items-center justify-between">
                          <h3 className="font-semibold text-foreground text-base font-default">
                            {stepItem.title}
                          </h3>
                          <IconComponent className="size-4 text-primary/70 transition-colors group-hover:text-primary" />
                        </div>

                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {stepItem.desc}
                        </p>

                        <div className="pt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-foreground">
                          {stepItem.points.map((point, pointIndex) => <span key={pointIndex} className="inline-flex items-center gap-1.5 text-muted-foreground">
                              <span className="h-1.5 w-1.5 rounded-lg bg-primary shrink-0" />
                              {point}
                            </span>)}
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>

            {/* Seamless Project Planning & Execution Card */}
            <div className="rounded-xl border border-primary/25 bg-primary/5 p-5 sm:p-6 shadow-2xs">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex size-10 sm:size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground font-semibold shrink-0">
                  <ArrowRight className="size-5" />
                </div>
                <div className="space-y-1.5 grow">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-foreground font-default">
                      Project Planning &amp; Contractor Implementation
                    </h3>
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      Next Stage
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    The audit is the launchpad, not the finish line. CMI helps businesses move seamlessly from audit findings into capital grant investigation, competitive specialist quotations, rigorous project planning, and coordinated turnkey contractor implementation.
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Actions Bar */}
            <div className="pt-2 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
              <Button asChild size="lg" className="shadow-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/SeaiEnergyAuditAndEligibilityPage" className="inline-flex items-center justify-center gap-2">
                  Check SEAI eligibility
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="secondary" size="lg" className="font-semibold">
                <Link to="/ContactPage">
                  Book an auditor consultation
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="font-semibold">
                <Link to="/SeaiGrantsCommercialEnergyUpgradesIrelandPage">
                  See Business Grant Supports
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}